import React from 'react';
import { Company } from '../../../../../types/company';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t, translate } from '../../../../../types/translation/Translator';
import { Collapsable } from '../../../Collapsable';
import { FeatureToggleItem, FeatureToggleTitle, FeatureToggleExplanation, FeatureToggleInformation } from '../common';

interface OrderFeatureTogglesViewProps {
  company: Company;
  update: (company: Company) => Promise<void>;
}

export default function OrderFeatureTogglesView({ company, update }: OrderFeatureTogglesViewProps) {
  const copy = new Company(company);
  const toggles = copy.settings.featureToggles.orders;

  return (
    <Collapsable
      open={toggles.orders}
      header={
        <FeatureToggleItem
          value={toggles.orders}
          onChange={async value => await update(copy.withOrdersOrderFeatureToggle(value))}
        >
          <FeatureToggleTitle text={t(TranslationKey.orderOrderFeatureToggleTitle).singular.upper} />
          <FeatureToggleExplanation text={t(TranslationKey.orderOrderFeatureToggleExplanation).singular.upper} />
          <FeatureToggleInformation text={t(TranslationKey.orderOrderFeatureToggleInformation).singular.upper} />
        </FeatureToggleItem>
      }
    >
      <FeatureToggleItem
        indent={1}
        value={toggles.customer}
        onChange={async value => await update(copy.withOrdersCustomer(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderCustomerFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.orderCustomerFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.supplier}
        onChange={async value => await update(copy.withOrdersSupplier(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderSupplierFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.orderSupplierFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.picking}
        onChange={async value => await update(copy.withOrdersPicking(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderPickingFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation text={t(TranslationKey.orderPickingFeatureToggleExplanation).singular.upper} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.sendMailOnAssigned}
        onChange={async value => await update(copy.withOrderSendMailOnAssigned(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderSendMailOnAssignedFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.orderSendMailOnAssignedFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.quickOrder}
        onChange={async value => await update(copy.withOrdersQuickOrder(value))}
      >
        <FeatureToggleTitle text={translate({ key: TranslationKey.orderQuickOrderFeatureToggleTitle })} />
        <FeatureToggleExplanation text={translate({ key: TranslationKey.orderQuickOrderFeatureToggleExplanation })} />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.editProductsOnInbound}
        onChange={async value => await update(copy.withOrdersEditProductsOnInbound(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderEditProductsOnInboundFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.orderEditProductsOnInboundFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.editProductsOnOutbound}
        onChange={async value => await update(copy.withOrdersEditProductsOnOutbound(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderEditProductsOnOutboundFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.orderEditProductsOnOutboundFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.editProductsOnMove}
        onChange={async value => await update(copy.withOrdersEditProductsOnMove(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderEditProductsOnMoveFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.orderEditProductsOnMoveFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.editProductsOnDisposal}
        onChange={async value => await update(copy.withOrdersEditProductsOnDisposal(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderEditProductsOnDisposalFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.orderEditProductsOnDisposalFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.processCompletedOrders}
        onChange={async value => await update(copy.withOrdersProcessCompletedOrders(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.ordersProcessCompletedOrdersFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.ordersProcessCompletedOrdersFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.productSelectionFirst}
        onChange={async value => await update(copy.withOrdersProductSelectionFirst(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.orderProductSelectionFirstFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.orderProductSelectionFirstFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      <FeatureToggleItem
        indent={1}
        value={toggles.ignoreAvailableQuantity}
        onChange={async value => await update(copy.withOrdersIgnoreAvailableQuantity(value))}
      >
        <FeatureToggleTitle
          text={t(TranslationKey.orderIgnoreAvailableQuantityWhenCreatingOrdersFeatureToggleTitle).singular.upper}
        />
        <FeatureToggleExplanation
          text={t(TranslationKey.orderIgnoreAvailableQuantityWhenCreatingOrdersFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>

      {company.settings.featureToggles.containers.containers && (
        <FeatureToggleItem
          indent={1}
          value={!toggles.productBasedApproach}
          onChange={async value => await update(copy.withOrdersProductBasedApproach(!value))}
        >
          <FeatureToggleTitle text={t(TranslationKey.orderContainerBasedApproachFeatureToggleTitle).singular.upper} />
          <FeatureToggleExplanation
            text={t(TranslationKey.orderContainerBasedApproachFeatureToggleExplanation).singular.upper}
          />
        </FeatureToggleItem>
      )}
    </Collapsable>
  );
}
